// ErrorScreen.jsx
import React, { useEffect, useState } from "react"
import { Container, Row, Col, Button, Form, Alert } from "react-bootstrap"
import { FiAlertCircle, FiSend, FiRefreshCw } from "react-icons/fi"
import { useWebProvider } from "../hooks/defaults/useWebProvider"
import { useSelector } from "react-redux"

const ErrorScreen = ({ errorInfo, onRetry }) => {
	const [showFeedbackForm, setShowFeedbackForm] = useState(false)
	const [feedback, setFeedback] = useState("")
	const [submitStatus, setSubmitStatus] = useState(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const { controller } = useWebProvider()
	const { userId, userName, setor, company, companyId } = useSelector(
		(state) => state.global
	)
	const userController = controller("user")
	useEffect(() => {
		const handlePopState = () => {
			onRetry();
		};
	
		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);
	
	const handleSendErrorReportNodemailer = async () => {
		setIsSubmitting(true)
		try {
			const errorReportForm = {
				userId,
				dataAbertura: new Date().toISOString(),
				errorInfo: errorInfo,
				userFeedback: feedback,
				userAgent: navigator.userAgent,
				url: window.location.href,
				userName,
				setor,
				company,
				companyId
			}

			const response = await userController.post(
				"errorReport",
				errorReportForm
			)

			if (!response) throw new Error("Falha no envio do relatório")

			setSubmitStatus("success")
			setShowFeedbackForm(false)
			setFeedback("")
		} catch (error) {
			console.error("Erro ao enviar relatório:", error)
			setSubmitStatus("error")
		} finally {
			setIsSubmitting(false)
		}
	}
	const getFriendlyMessage = (error) => {
		if (!error || !error.status) return "Erro inesperado."
		switch (error.status) {
			case 400:
				return "Requisição inválida."
			case 401:
				return "Você precisa estar logado."
			case 403:
				return "Você não tem permissão para acessar isso."
			case 404:
				return "Recurso não encontrado."
			case 500:
				return "Erro interno no servidor. Tente novamente mais tarde."
			default:
				return "Ocorreu um erro inesperado."
		}
	}

	return (
		<Container
			className="d-flex justify-content-center align-items-center"
			style={{ minHeight: "100vh", padding: "2rem" }}
		>
			<Row className="text-center w-100" style={{ maxWidth: "800px" }}>
				<Col>
					<div hidden={!errorInfo}>
						<FiAlertCircle
							size={100}
							color="#dc3545"
							className="mb-4"
						/>
					</div>

					<h1 hidden={!errorInfo} className="mb-4">
						{getFriendlyMessage(errorInfo)}
					</h1>

					<p className="lead text-muted mb-4">
						Parece que algo não está funcionando corretamente. Por
						favor, nos ajude reportando o problema.
					</p>

					{submitStatus === "success" && (
						<Alert variant="success" className="mb-4">
							Obrigado pelo seu feedback! Isso nos ajudará a
							melhorar a aplicação.
						</Alert>
					)}

					{submitStatus === "error" && (
						<Alert variant="danger" className="mb-4">
							Não foi possível enviar o relatório. Por favor,
							tente novamente.
						</Alert>
					)}

					<div className="d-grid gap-3 d-sm-flex justify-content-sm-center mb-4">
						<div hidden={!errorInfo}>
							<Button
								variant="primary"
								size="lg"
								onClick={onRetry}
								className="d-flex align-items-center justify-content-center gap-2"
							>
								<FiRefreshCw />
								Tentar Novamente
							</Button>
						</div>

						<Button
							variant="outline-secondary"
							size="lg"
							onClick={() =>
								setShowFeedbackForm(!showFeedbackForm)
							}
							className="d-flex align-items-center justify-content-center gap-2"
						>
							<FiSend />
							Reportar Problema
						</Button>
					</div>

					{showFeedbackForm && (
						<div className="mt-4">
							<Form.Group className="mb-3">
								<Form.Label>
									Nos conte o que aconteceu:
								</Form.Label>
								<Form.Control
									as="textarea"
									rows={4}
									value={feedback}
									onChange={(e) =>
										setFeedback(e.target.value)
									}
									placeholder="Descreva o que você estava fazendo quando o erro ocorreu..."
									disabled={isSubmitting}
								/>
							</Form.Group>
							<Button
								variant="danger"
								onClick={handleSendErrorReportNodemailer}
								disabled={isSubmitting || !feedback.trim()}
							>
								{isSubmitting
									? "Enviando..."
									: "Enviar Relatório"}
							</Button>
						</div>
					)}

					{process.env.NODE_ENV === "development" && (
						<div className="mt-5">
							<details className="text-start">
								<summary className="cursor-pointer mb-2">
									Detalhes técnicos do erro
								</summary>
								<pre
									className="bg-light p-3 rounded"
									style={{ whiteSpace: "pre-wrap" }}
								>
									{JSON.stringify(errorInfo, null, 2)}
								</pre>
							</details>
						</div>
					)}
				</Col>
			</Row>
		</Container>
	)
}

export default ErrorScreen
